import type { DeviceStatusExpression } from '@/types/deviceStatus'
import { isDemoDeployment } from '@/utils/deployment'
import { apiCall } from './axiosInstance'
import { derivedDeviceStateApi as demoDerivedDeviceStateApi } from './demo/derivedDeviceStateApi'

/**
 * A derived device state: a named device_state column whose value is the first of `expressions`
 * whose condition a device matches.
 *
 * `expressions` is opaque JSONB to the backend — it neither validates nor evaluates it today, so
 * the icon and color the UI renders each state with travel inside it.
 */
export interface DerivedDeviceState {
	id: number
	name: string
	expressions: DeviceStatusExpression
}

export type DerivedDeviceStateParams = Omit<DerivedDeviceState, 'id'>

export interface DerivedDeviceStateCreatedResponse {
	id: number
}

export interface DerivedDeviceStateStatusResponse {
	status: string
}

const tenantHeaders = (tenantId?: string, impersonateAdmin = true) =>
	tenantId
		? {
				headers: {
					'x-bytebeam-tenant': tenantId,
					...(impersonateAdmin ? { 'x-bytebeam-impersonate': 'admin' } : {}),
				},
			}
		: undefined

const v3DerivedDeviceStateApi = {
	getDerivedDeviceStates: async (tenantId?: string, impersonateAdmin = true): Promise<DerivedDeviceState[]> => {
		const states = await apiCall<DerivedDeviceState[]>(
			'/v3/derived-device-state',
			tenantHeaders(tenantId, impersonateAdmin),
		)
		return Array.isArray(states) ? states : []
	},

	createDerivedDeviceState: async (
		params: DerivedDeviceStateParams,
		tenantId?: string,
	): Promise<DerivedDeviceStateCreatedResponse> =>
		await apiCall('/v3/derived-device-state', {
			method: 'POST',
			data: params,
			...tenantHeaders(tenantId),
		}),

	updateDerivedDeviceState: async (
		id: number,
		params: DerivedDeviceStateParams,
		tenantId?: string,
	): Promise<DerivedDeviceStateStatusResponse> =>
		await apiCall(`/v3/derived-device-state/${id}`, {
			method: 'PATCH',
			data: params,
			...tenantHeaders(tenantId),
		}),

	deleteDerivedDeviceState: async (id: number, tenantId?: string): Promise<DerivedDeviceStateStatusResponse> =>
		await apiCall(`/v3/derived-device-state/${id}`, {
			method: 'DELETE',
			...tenantHeaders(tenantId),
		}),
}

export const derivedDeviceStateApi = isDemoDeployment() ? demoDerivedDeviceStateApi : v3DerivedDeviceStateApi
